/**
 * React Query hook for investor portal dashboard data
 */

import { useApiQuery } from '@/hooks/react-query/useApiQuery';
import { portalQueryKeys } from '@/portal/shared/hooks/queryKeys';
import { investorPortalApiClient } from '@/portal/shared/utils/api-client';

interface DashboardContact {
  first_name: string;
  last_name: string;
  email: string;
  investor_name: string;
}

interface DashboardFund {
  id: string;
  name: string;
  display_name: string;
  vintage_year: number;
  status: string;
  base_currency: string;
  commitment_amount: number;
  total_called: number;
  total_distributed: number;
  information_as_of: string | null;
}

interface DashboardSummary {
  total_commitment: number;
  total_called: number;
  total_contributed: number;
  total_distributed: number;
  uncalled_commitment: number;
  net_contributions: number;
  nav: number | null;
  unrealized_gain_loss: number | null;
  base_currency: string | null;
  information_as_of: string | null;
  holdings_published: boolean;
  dashboard_published: boolean;
}

interface DashboardPerformance {
  dpi: number;
  moic: number;
  irr: number;
  total_contributed: number;
  total_distributed: number;
}

export interface DashboardData {
  contact: DashboardContact;
  summary: DashboardSummary;
  performance: DashboardPerformance;
  funds: DashboardFund[];
}

/**
 * Hook to fetch dashboard data for the investor portal
 */
export const useDashboard = () => {
  return useApiQuery<DashboardData>(
    portalQueryKeys.dashboard.summary(),
    () => investorPortalApiClient.get<DashboardData>('/portal/dashboard'),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );
};
